import { CurrencyPipe, DatePipe, LowerCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { GridAssignedToIdFilterComponent } from 'src/app/shared/grid/filters/grid-assigned-to-id-filter.component';
import { GridColumnDirective } from 'src/app/shared/grid/grid-column.directive';
import { GridComponent } from 'src/app/shared/grid/grid.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FetchArgs } from '../../models/core';
import { ListItem } from '../../models/list-item.model';
import { Expertise, GridLookups, ProfessionsLookups, Statuses } from '../../models/offers.model';

@Component({
    selector: 'nova-shared-offers-grid',
    templateUrl: './offers-grid.component.html',
    styleUrls: ['./offers-grid.component.scss'],
    standalone: true,
    imports: [
        GridComponent,
        GridColumnDirective,
        GridAssignedToIdFilterComponent,
        MatLegacyButtonModule,
        MatTooltipModule,
        DatePipe,
        LowerCasePipe,
        CurrencyPipe,
        RouterModule
    ]
})
export class SharedOffersGridComponent {
    readonly featureFlag = FeatureFlag;
    @ViewChild('grid', { static: true }) grid: GridComponent;
    @Input() gridData: GridDataResult;
    @Input() set fetchArgs(args: FetchArgs) {
        if (args) {
            this.args = args;
        }
    }

    @Input() isLoading: boolean;

    @Input() set lookups(data: GridLookups) {
        if (data) {
            this.lookupStatuses = data.statuses;
            this.lookupProfessions = data.professions;
            this.filteredStatuses = this.lookupStatuses;
            this.contractReqStatuses = data.contractRequestStatuses;
            this.accountManagerValues = data.accountManagers;
            this.recruiterLookupValues = data.recruiters;
            this.teamManagerLookupValues = data.teamManagers;

            this.contractStages = data.contractStages;
            data.expertises.forEach((exp) => {
                this.lookupExpertise.push({
                    id: exp.id,
                    name: exp.name,
                    abbreviation: exp.abbreviation,
                    sort: exp.sort,
                    professionExpertiseCodes: exp.professionExpertiseCodes,
                    searchableVal: exp.name
                });
            });
        }
    }
    @Input() rowClass: (row: RowClassArgs) => unknown = () => null;
    @Input() userType: OffersUserType;
    @Input() autoFitColumnsVal: boolean = false;
    @Input() userId: number;
    @Input() assignedToField: string;
    @Input() perPageItems: boolean;
    @Input() byId: boolean;
    @Input() hyperLinkCandidate: boolean = false;

    @Output() filterChange: EventEmitter<any> = new EventEmitter();
    @Output() gridChange: EventEmitter<any> = new EventEmitter();
    @Output() argsEmit: EventEmitter<any> = new EventEmitter();
    @Output() viewDeatilsClick: EventEmitter<number> = new EventEmitter();
    @Output() openFacilityLink: EventEmitter<number> = new EventEmitter();
    @Output() openCandidateProfile: EventEmitter<number> = new EventEmitter();
    @Output() shareOfferAsVerballyAccepted: EventEmitter<number> = new EventEmitter();

    lookupStatuses: Statuses[];
    filteredStatuses: Statuses[];
    contractReqStatuses: ListItem[];
    accountManagerValues: ListItem[];
    recruiterLookupValues: ListItem[];
    teamManagerLookupValues: ListItem[];
    contractStages: ListItem[];
    lookupProfessions: ProfessionsLookups[];
    lookupExpertise: Expertise[] = [];
    offersUserType = OffersUserType;
    args: FetchArgs;
    private readonly _featureManager: LDFeatureManager = inject(LDFeatureManager);

    isOpenViewDetailsInNewTab = this._featureManager.isEnabledSignal(FeatureFlag.IsOpenViewDetailsInNewTab);

    load(args: FetchArgs) {
        this.args = args;
        this.argsEmit.emit(this.args);
    }

    isFilterValuePresent(value) {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return !!value;
    }

    dataGridChanged(event) {
        this.gridChange.emit(event);
    }

    filterChanged($event) {
        this.argsEmit.emit($event);
    }

    openFacility(facilityId: number) {
        this.openFacilityLink.emit(facilityId);
    }

    viewDetails(id: number) {
        this.viewDeatilsClick.emit(id);
    }

    candidateClick(candidateId: number) {
        this.openCandidateProfile.emit(candidateId);
    }

    onChangeFilter(filter) {
        this.argsEmit.emit(this.args);
    }

    verballyAcceptOffer(dataItem: any): void {
        this.shareOfferAsVerballyAccepted.emit(dataItem.id);
    }
}
